import type { CastComment, CastCommentThread } from './types';

/**
 * The attribute that names a user on `<ins>` / `<del>` and on `<comment>`. One
 * name for both, so the model can tell when a commenter also made a tracked
 * change; the format prompt (`createCastContextPrompt`) tells it so.
 */
export const AUTHOR_ID_ATTRIBUTE_NAME = 'author-id';

/**
 * The tag names for comments in serialized document context. Single source of
 * truth: the serializer (`commentMarkers.ts`) prints them, and the format prompt
 * (`createCastContextPrompt`) and the editor prompt document them.
 */
export const COMMENT_TAG_NAMES = {
  comment: 'comment',
  end: 'comment-end',
  start: 'comment-start',
  thread: 'comment-thread',
} as const;

/**
 * The attribute names on `<comment>`, keyed by the `CastComment` property each
 * one prints. Every property but the text needs one, so a new property fails to
 * compile until it gets a name. The values are wire format, spelled out for the
 * reason given on `LIST_ATTRIBUTE_NAMES`.
 */
export const COMMENT_ATTRIBUTE_NAMES = {
  authorId: AUTHOR_ID_ATTRIBUTE_NAME,
  authorName: 'author',
  createdAt: 'date',
  party: 'party',
} as const satisfies Record<Exclude<keyof CastComment, 'text'>, string>;

/** The attribute names on `<comment-thread>`, keyed like `COMMENT_ATTRIBUTE_NAMES`. */
export const COMMENT_THREAD_ATTRIBUTE_NAMES = {
  id: 'id',
  isPrivate: 'private',
} as const satisfies Record<Exclude<keyof CastCommentThread, 'comments'>, string>;

/** The attribute names on `<comment-start/>` and `<comment-end/>`. */
export const COMMENT_MARKER_ATTRIBUTE_NAMES = {
  continuesAfter: 'continues-after',
  continuesBefore: 'continues-before',
  id: 'id',
} as const;
